import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { workspace } from "@/lib/dashboard-data";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({ meta: [{ title: "Sign in" }] }),
});

function friendlyAuthError(raw: string) {
  if (/invalid account|invalid password|incorrect/i.test(raw))
    return "Invalid email or password.";
  if (/already exists|already registered/i.test(raw))
    return "An account with this email already exists. Try signing in instead.";
  if (/password.*8|too short|weak/i.test(raw))
    return "Password must be at least 8 characters.";
  if (/email.*invalid|invalid email/i.test(raw))
    return "Please enter a valid email address.";
  return raw || "Something went wrong. Please try again.";
}

function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { signIn } = useAuthActions();
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate({ to: "/", replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signIn("password", {
        email: email.trim(),
        password,
        flow: mode,
        ...(mode === "signUp" && name.trim() ? { name: name.trim() } : {}),
      });
      navigate({ to: "/", replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? friendlyAuthError(err.message)
          : "Something went wrong. Please try again.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="flex min-h-svh w-full items-center justify-center bg-background px-6 py-10 [@media(max-height:480px)]:items-start [@media(max-height:480px)]:py-4">
      <div className="flex w-full max-w-sm flex-col gap-6 [@media(max-height:480px)]:gap-4">
        <div className="flex items-center justify-center gap-2">
          <img src="/logo.png" alt="" className="size-8 shrink-0" />
          <span className="text-base font-medium">{workspace.name} HQ</span>
        </div>

        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-xl font-semibold tracking-tight">
            {mode === "signIn"
              ? `Sign in to ${workspace.name} HQ`
              : `Create your ${workspace.name} HQ account`}
          </h1>
          {mode === "signUp" && (
            <p className="text-sm text-muted-foreground">
              New accounts need admin approval before they can sign in.
            </p>
          )}
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <FieldGroup className="gap-4">
            {mode === "signUp" && (
              <Field className="gap-2">
                <FieldLabel htmlFor="auth-name">Name</FieldLabel>
                <Input
                  id="auth-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
            )}
            <Field className="gap-2">
              <FieldLabel htmlFor="auth-email">Email</FieldLabel>
              <Input
                id="auth-email"
                type="email"
                required
                autoComplete={mode === "signIn" ? "username" : "email"}
                placeholder="you@veez.io"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>

            <Field className="gap-2">
              <FieldLabel htmlFor="auth-password">Password</FieldLabel>
              <InputGroup>
                <InputGroupInput
                  id="auth-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete={mode === "signIn" ? "current-password" : "new-password"}
                  placeholder={mode === "signIn" ? "Enter your password" : "Choose a password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    variant="ghost"
                    size="icon-xs"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" aria-hidden="true" />
                    ) : (
                      <Eye className="size-4" aria-hidden="true" />
                    )}
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
              {mode === "signUp" ? (
                <FieldDescription>Use at least 8 characters.</FieldDescription>
              ) : null}
            </Field>
          </FieldGroup>

          {error && (
            <p role="alert" className="text-sm text-destructive wrap-break-word">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" className="w-full" disabled={pending}>
            {pending ? "Please wait…" : mode === "signIn" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          {mode === "signIn" ? "Need an account? " : "Have an account? "}
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            onClick={() => {
              setMode(mode === "signIn" ? "signUp" : "signIn");
              setError(null);
            }}
          >
            {mode === "signIn" ? "Create one" : "Sign in"}
          </Button>
        </p>
      </div>
    </main>
  );
}
